// =============================================
// File: refund/page.jsx
// Description: Refunds & Cancellations Page (React / Next.js)
// =============================================

'use client';

import React from 'react';
import Link from 'next/link';
import Navbar from '@components/Navbar';
import styles from './refund.module.css';
import Footer from '@components/footer/Footer';

export default function RefundPage() {
  return (
    <>
      <Navbar />
      <main className={styles.page}>
        <section className={styles.hero}>
          <div className={styles.heroInner}>
            <h1 className={styles.title}>Refunds & Cancellations</h1>
            <p className={styles.sub}>
              Clear, fair, and kid-first. No memberships, no surprises — you only pay for the classes you choose.
            </p>
          </div>
        </section>

        <nav className={styles.breadcrumbs} aria-label="Breadcrumb">
          <ol>
            <li><Link href="/">Home</Link></li>
            <li aria-current="page">Refunds</li>
          </ol>
        </nav>

        <section className={styles.quick}>
          <h2>Quick summary</h2>
          <ul className={styles.highlights}>
          We want every family to feel confident when enrolling.
          <br />
          <br />
          • Cancel at least <strong>24 hours before</strong> the first live session for a full refund to your original payment method.
          <br />
          • After the first session, you’ll receive a refund for the remaining balance if you paid in full.
          <br />
          • If a scheduling conflict happens after enrollment and you paid in full, we’ll also refund your remaining balance.
          <br />
          </ul>
          <p className={styles.note}>
            *Refund method may vary for Education Savings Accounts (ESA) based on state processor rules.
          </p>
        </section>

        <section className={styles.grid}>
          <article className={styles.card}>
            <h3>Standard classes (live, small-group)</h3>
            <ul>
              <li><strong>Before first live meeting:</strong> Full refund to the original payment method (or ESA per processor rules).</li>
              <li>
                <strong>After first meeting:</strong> Full refund if the class isn’t a fit.
                If there’s a time conflict and you paid in full, we’ll refund the remaining balance.
              </li>
              <li><strong>Teacher cancels or no class delivered:</strong> Full refund — our treat.</li>
            </ul>
          </article>

          <article className={styles.card}>
            <h3>Teacher cancellations</h3>
            <ul>
              <li>If an educator cancels a session or course, you’ll receive a <strong>full refund</strong>.</li>
            </ul>
          </article>

          <article className={styles.card}>
            <h3>What if a class is rescheduled?</h3>
            <ul>
              <li>If the new time doesn’t work, you can choose a <strong>full refund</strong>.</li>
            </ul>
          </article>

          <article className={styles.card}>
            <h3>Processing times</h3>
            <ul>
              <li>Card refunds: typically 3–10 business days (bank dependent).</li>
              <li>ESA refunds: timing depends on the state vendor system and processor.</li>
            </ul>
          </article>

          <article className={styles.card}>
            <h3>ESA payments</h3>
            <ul>
              <li>We’ll follow the rules of <em>your</em> ESA program (ClassWallet, Odyssey, SFE, etc.).</li>
            </ul>
          </article>

          <article className={styles.card}>
            <h3>Abuse & safety</h3>
            <ul>
              <li>To protect families and educators, we may deny refunds for misuse, repeated no-shows, or policy violations.</li>
            </ul>
          </article>
        </section>

        <section className={styles.how}>
          <h2>How to request a refund</h2>
          <ol className={styles.steps}>
            <li>
              <a href="mailto:support@penguinlearn.com" className={styles.btnSmall}>
                Email Support
              </a>
            </li>
            <li>Tell us the class name, section time and your student name.</li>
            <li>Tell us the reason. Our team reviews within one business day.</li>
          </ol>
        </section>

        <aside className={styles.ctaBlock}>
          <div className={styles.ctaInner}>
            <h2>We’re here to help</h2>
            <p>Tell us what happened and we’ll make it right.</p>
            <div className={styles.actions}>
              <a href="mailto:support@penguinlearn.com" className={styles.btn}>Email Support</a>
            </div>
          </div>
        </aside>
      </main>
      <Footer />
    </>
  );
}
